import { useCallback, useState } from "react";
import HeroSlider from "./HeroSlider";
import HeroVideo from "./HeroVideo";
import GraduationSlider from "./GraduationSlider";
import "./Hero1.css";
import "./HeroAnimations.css";
import HeroFilters from "./Herofilter";

// Graduation campaign expiry — 14 Dec 2026 (3 days after graduation on 11 Dec 2026).
// When expired Hero.jsx skips straight to the main slider.
const GRADUATION_EXPIRY = new Date("2026-12-14T23:59:59");
const graduationActive = () => Date.now() <= GRADUATION_EXPIRY.getTime();

// Hero reel (when graduation campaign is live):
//   video → graduation-slider → main-slider → video → …
//
// After 14 Dec 2026:
//   video → main-slider → video → …
//
// stage values:
//   "video"        — HeroVideo
//   "graduation"   — GraduationSlider  (4 graduation slides)
//   "slides"       — HeroSlider      (main promo carousel)

export default function Hero() {
    const [stage,      setStage]      = useState("video");
    const [entrySlide, setEntrySlide] = useState(0);

    // Remount keys so each component restarts cleanly on re-entry
    const [videoRun,       setVideoRun]       = useState(0);
    const [graduationRun,  setGraduationRun]  = useState(0);
    const [sliderRun,      setSliderRun]      = useState(0);

    // ── Helpers ───────────────────────────────────────────────────────────────
    const openGraduation = useCallback(() => {
        setGraduationRun((n) => n + 1);
        setStage("graduation");
    }, []);

    const openSlider = useCallback((index) => {
        setEntrySlide(index);
        setSliderRun((n) => n + 1);
        setStage("slides");
    }, []);

    const backToVideo = useCallback(() => {
        setVideoRun((n) => n + 1);
        setStage("video");
    }, []);

    // ── Video callbacks ───────────────────────────────────────────────────────
    const handleVideoEnd = useCallback(() => {
        if (graduationActive()) { openGraduation(); } else { openSlider(0); }
    }, [openGraduation, openSlider]);

    const handleVideoNext = useCallback(() => {
        if (graduationActive()) { openGraduation(); } else { openSlider(0); }
    }, [openGraduation, openSlider]);

    // Stepping back from video → last main slide
    const handleVideoPrev = useCallback(() => {
        openSlider(-1);
    }, [openSlider]);

    // ── Graduation slider callbacks ───────────────────────────────────────────
    // Finished all 4 slides → main slider
    const handleGraduationEnd = useCallback(() => {
        openSlider(0);
    }, [openSlider]);

    // Arrow-left off slide 1 → back to video
    const handleGraduationRequestVideo = useCallback(() => {
        backToVideo();
    }, [backToVideo]);

    // ── Render ────────────────────────────────────────────────────────────────
    const isVideo       = stage === "video";
    const isGraduation  = stage === "graduation";
    const isSlider      = stage === "slides";

    return (
        <>
            <section className={`hero ${isVideo ? "hero--video" : "hero--slider"}`}>

                {isVideo && (
                    <HeroVideo
                        key={videoRun}
                        onVideoEnd={handleVideoEnd}
                        onNext={handleVideoNext}
                        onPrev={handleVideoPrev}
                    />
                )}

                {isGraduation && (
                    <GraduationSlider
                        key={graduationRun}
                        onCycleEnd={handleGraduationEnd}
                        onRequestVideo={handleGraduationRequestVideo}
                    />
                )}

                {isSlider && (
                    <HeroSlider
                        key={sliderRun}
                        initialIndex={entrySlide}
                        onCycleEnd={backToVideo}
                        onRequestVideo={backToVideo}
                    />
                )}

                <div className="overlay"></div>

                {/* Floating Particles */}
                <div className="hero-particles">
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                </div>
            </section>

            <HeroFilters />
        </>
    );
}
